/**
 * Questionnaire API calls
 */
import axios, { type AxiosProgressEvent } from 'axios';
import { getQuestionnaireApiBase, getApiHeaders, getApiAuthHeaders } from './api';
import { getAccessToken } from '../../../shared/auth/okta';
import type { QAResponse } from '../types/qa';

// --- Question answers (QAF) ---

/**
 * Paginated Q&A list.
 * GET /api/questionnaire/question-answers
 */
export async function getQuestionAnswers(params: URLSearchParams): Promise<QAResponse> {
  const accessToken = getAccessToken();
  const { data } = await axios.get<QAResponse>(
    `${getQuestionnaireApiBase()}/question-answers?${params.toString()}`,
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

export interface PatchQuestionAnswerBody {
  feedback_rating?: string | null;
  feedback_reason?: string | null;
  user_edited_ans?: string | null;
  admin_edited_ans?: string | null;
  admin_status?: string | null;
  approved_by_admin?: string | null;
}

/**
 * Update a question–answer–feedback row.
 * PATCH /api/questionnaire/question-answers/{qaf_id}
 */
export async function patchQuestionAnswer(
  qafId: string,
  body: PatchQuestionAnswerBody
): Promise<unknown> {
  const accessToken = getAccessToken();
  const { data } = await axios.patch(
    `${getQuestionnaireApiBase()}/question-answers/${qafId}`,
    body,
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

// --- RAG / knowledge base ---
export type RagUploadRequestBody = {
  delete_previous_day: boolean;
  test_mode_seconds: number | null;
};

export type RagUploadResponse = {
  success: boolean;
  uploaded_count: number;
  total_count: number;
  errors: string[];
  message?: string | null;
  gcs_files?: string[] | null;
  previous_day_deletion?: Record<string, unknown> | null;
};

/**
 * Upload approved Q&A pairs to the RAG corpus.
 * POST /api/questionnaire/rag/approved-qa
 */
export async function uploadApprovedQaToRag(
  body: RagUploadRequestBody = {
    delete_previous_day: true,
    test_mode_seconds: null,
  }
): Promise<RagUploadResponse> {
  const accessToken = getAccessToken();
  const { data } = await axios.post<RagUploadResponse>(
    `${getQuestionnaireApiBase()}/rag/approved-qa`,
    body,
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

// --- Extract questions ---

export interface ExtractQuestionsApiResponse {
  success: boolean;
  questions: Array<{
    sheet_name?: string;
    row_index?: number;
    question_id: string;
    question_number: string;
    question_text: string;
    question_locale?: string | null;
  }>;
  total_questions: number;
  filename: string;
  message: string;
  file_uuid?: string;
}

/**
 * Extract questions from an uploaded document.
 * POST /api/questionnaire/questions/extract
 */
export async function postExtractQuestions(
  formData: FormData,
  options?: { onUploadProgress?: (e: AxiosProgressEvent) => void }
): Promise<ExtractQuestionsApiResponse> {
  const accessToken = getAccessToken();
  const { data } = await axios.post<ExtractQuestionsApiResponse>(
    `${getQuestionnaireApiBase()}/questions/extract`,
    formData,
    {
      headers: getApiAuthHeaders(accessToken),
      onUploadProgress: options?.onUploadProgress,
    }
  );
  return data;
}

// --- Export file ---

/**
 * Full URL for GET /api/questionnaire/files/export (use with fetch + Bearer-only headers).
 */
export function getQuestionnaireExportFileUrl(params: URLSearchParams): string {
  return `${getQuestionnaireApiBase()}/files/export?${params.toString()}`;
}

export type TranslateAnswerTextRequestBody = {
  text: string;
  target_locale: string;
  source_locale?: string;
  qaf_id?: string;
};

export type TranslateAnswerTextResponseBody = {
  translated_text: string;
};

/**
 * POST /api/questionnaire/answers/translate-text
 */
export async function translateAnswerText(
  body: TranslateAnswerTextRequestBody
): Promise<TranslateAnswerTextResponseBody> {
  const accessToken = getAccessToken();
  const { data } = await axios.post<TranslateAnswerTextResponseBody>(
    `${getQuestionnaireApiBase()}/answers/translate-text`,
    body,
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

// --- SSE stream ---

/**
 * POST endpoint for streaming answers (SSE).
 */
export function getQuestionnaireAnswersStreamUrl(): string {
  return `${getQuestionnaireApiBase()}/answers/stream`;
}
